// War HUD (GDD §14, docs/ARCHITECTURE.md §8): a DOM overlay over the war canvas. It reads the war only through
// MatchView callbacks (change-driven: private state, events, city changes, the result) and acts only through
// MatchView commands. It never predicts: local checks shape hints, and the sim's commandRejected is the verdict.
// Details live in the Intel panel (infoPanel.ts): it describes the tile or build option under the pointer or focus,
// else the city under the pointer on the globe. The HUD uses no browser tooltips.
// Every listener, subscription and timer ends with the HUD's lifetime (the mount signal or dispose()).

import './hud.css'
import { BUILDING_KEYS, UNIT_KEYS, STRIKE_KEYS, keyOf, typeIdOf, type BuildingKey, type TaxSetting } from '@taxwar/content'
import type { CityIndex, MapCityView, PlayerId, PlayerPrivateState, RejectReason, RoomPresence, SimEvent, SimEventKind } from '@taxwar/net'
import type { LinkState, Unsubscribe } from '../net'
import type { GlobePicker, HudHandle, MatchView } from '../match/contracts'
import type { Palette } from '../render/palette'
import type { VisualSettings } from '../render/visualSettings'
import { PurchaseBar } from './bars'
import { BuildMenu } from './buildMenu'
import { BUILDING_PRESENTATION } from './catalog'
import { DeploySites } from './deploySites'
import { button, el, isTextEntry } from './dom'
import { EmptyCities } from './emptyCities'
import { HudError } from './errors'
import { HeadlineTicker, Toasts } from './feeds'
import { GlobePointer } from './gestures'
import { HeadlineWriter } from './headlines'
import { InfoPanel } from './infoPanel'
import { buildingCard, cityCard, strikeCard, unitCard, type InfoCard } from './intel'
import { ITEMS_BY_HOTKEY, STRIKE_ITEMS, UNIT_ITEMS, type BarItem, type StrikeItem, type UnitItem } from './items'
import { LedgerPanel } from './ledger'
import { REJECT_COPY } from './rejects'
import { buildObjection, buildingSlots } from './rules'
import { ApprovalMeter, TaxDial, TreasuryPanel, WarClock } from './status'
import { applyUiTheme } from './theme'
import { WarMapIndex } from './warMap'

/** Commands remembered for labelling their rejection toast; older ones are forgotten first. */
const PENDING_LABEL_CAP = 32
const PAUSE_LABEL = 'Pause [Esc]'
const RESUME_LABEL = 'Resume [Esc]'
const PAUSED_BANNER = 'PAUSED — the war is on hold. The invoices are not.'
const RECENTRE_KEY = 'Space'
const ESCAPE_KEY = 'Escape'
const TAX_CHANGE_LABEL = 'Tax change'
const SURRENDER_LABEL = 'Surrender'
const SURRENDER_CONFIRM_LABEL = 'Confirm surrender'
/** A first click arms Surrender for this long; a second click within it concedes. */
const SURRENDER_ARM_MS = 4000
const MS_PER_SECOND = 1000
/** The reconnect banner's countdown ticks once a second. */
const LINK_COUNTDOWN_PERIOD_MS = MS_PER_SECOND

/** While an online war rejoins its room after a drop: how long before the room forfeits the seat (net/room-connection.ts). */
function reconnectLine(giveUpAtMs: number, nowMs: number): string {
  const seconds = Math.max(0, Math.ceil((giveUpAtMs - nowMs) / MS_PER_SECOND))
  return `Connection to the war room lost. Reconnecting… Orders are on hold; the room forfeits your seat in ${seconds} s if you do not get back.`
}

/** The room's presence as one line for the banner, or null when there is nothing to say (docs/ARCHITECTURE.md §15). */
function presenceLine(presence: RoomPresence, you: PlayerId): string | null {
  if (presence.phase === 'gathering') return `Waiting for your rival to connect. The room closes in ${presence.abandonInSeconds} s if they do not.`
  if (presence.phase === 'starting') return `Both leaders present. The war begins in ${presence.startsInSeconds}…`
  for (let seat = 0; seat < presence.seats.length; seat++) {
    const seatPresence = presence.seats[seat]
    if (seat !== you && seatPresence?.kind === 'away') return `Rival connection lost. They surrender in ${seatPresence.surrenderInSeconds} s unless they return.`
  }
  return null
}

type EventOf<K extends SimEventKind> = Extract<SimEvent, { readonly kind: K }>

/** A HUD element the Intel panel describes: the item or build option under the pointer or keyboard focus. */
type HudSubject = { readonly kind: 'none' } | { readonly kind: 'item'; readonly item: BarItem } | { readonly kind: 'building'; readonly key: BuildingKey }
type SubjectOf<K extends HudSubject['kind']> = Extract<HudSubject, { readonly kind: K }>
type ItemOf<K extends BarItem['kind']> = Extract<BarItem, { readonly kind: K }>

const NO_SUBJECT: HudSubject = { kind: 'none' }

/** Whether an item's card shows its deploy-site verdict (units do; strikes launch without one). */
const ITEM_SHOWS_SITE: { readonly [K in BarItem['kind']]: boolean } = { unit: true, strike: false }

/** seq → what the player asked for, so a rejection toast can say which order bounced. Bounded FIFO. */
class PendingLabels {
  private readonly labels = new Map<number, string>()

  record(seq: number, label: string): void {
    if (this.labels.size >= PENDING_LABEL_CAP) {
      const oldest = this.labels.keys().next()
      if (!oldest.done) this.labels.delete(oldest.value)
    }
    this.labels.set(seq, label)
  }

  take(seq: number): string | null {
    const label = this.labels.get(seq)
    if (label === undefined) return null
    this.labels.delete(seq)
    return label
  }
}

export class Hud implements HudHandle {
  private readonly lifetime = new AbortController()
  private readonly element: HTMLElement
  private readonly map: WarMapIndex
  private readonly sites: DeploySites
  private readonly headlines: HeadlineWriter
  private readonly pending = new PendingLabels()
  private readonly subscriptions: readonly Unsubscribe[]

  private readonly treasury = new TreasuryPanel()
  private readonly approval = new ApprovalMeter()
  private readonly clock = new WarClock()
  private readonly ledger: LedgerPanel
  private readonly intel = new InfoPanel()
  private readonly arsenal: PurchaseBar<UnitItem>
  private readonly strikes: PurchaseBar<StrikeItem>
  private readonly taxDial: TaxDial
  private readonly ticker: HeadlineTicker
  private readonly toasts: Toasts
  private readonly buildMenu: BuildMenu
  private readonly emptyCities: EmptyCities
  private readonly pauseButton: HTMLButtonElement
  private readonly surrenderButton: HTMLButtonElement
  private readonly presenceBanner: HTMLElement
  private readonly linkBanner: HTMLElement
  private surrenderArmed: ReturnType<typeof setTimeout> | null = null
  /** The theme is the palette's colours with the settings' effects switch (theme.ts). */
  private settings: VisualSettings
  private palette: Palette

  /** A HUD element under the pointer or focus wins over the globe city under the pointer. */
  private hudSubject: HudSubject = NO_SUBJECT
  private globeCity: CityIndex | null = null
  private tax: TaxSetting | null = null
  private pauseInFlight = false
  private ended = false
  /** Online: the seat's link to the room is live (always, in a local war); orders wait while it reconnects. */
  private linkLive = true
  private linkGiveUpAtMs = 0
  private linkCountdown: ReturnType<typeof setInterval> | null = null
  private disposed = false

  constructor(
    root: HTMLElement,
    private readonly match: MatchView,
    private readonly picker: GlobePicker,
    settings: VisualSettings,
    palette: Palette,
    mountSignal: AbortSignal,
  ) {
    if (mountSignal.aborted) throw new HudError('HUD mounted with an already-aborted signal')
    this.settings = settings
    this.palette = palette
    const signal = this.lifetime.signal
    mountSignal.addEventListener('abort', () => this.dispose(), { once: true, signal })
    this.map = new WarMapIndex(match.setup)
    this.sites = new DeploySites(this.map, match)
    this.headlines = new HeadlineWriter(this.map)

    this.taxDial = new TaxDial(this.selectTax, signal)
    this.ticker = new HeadlineTicker(signal)
    this.toasts = new Toasts(signal)
    this.buildMenu = new BuildMenu(this.map, match, this.build, this.hoverBuilding, signal)
    this.emptyCities = new EmptyCities(this.map, match, this.openEmptyCity, this.closeCityPanels, signal)
    this.ledger = new LedgerPanel({ settings, onOpen: this.ledgerOpened, floorY: () => this.element.getBoundingClientRect().bottom, signal })
    this.arsenal = new PurchaseBar<UnitItem>('Arsenal', UNIT_ITEMS, this.buy, this.hoverItem, signal)
    this.strikes = new PurchaseBar<StrikeItem>('Strikes', STRIKE_ITEMS, this.buy, this.hoverItem, signal)

    this.element = el('div', 'tw-hud')
    applyUiTheme(this.element, palette, settings)

    this.pauseButton = button('tw-button tw-hud__pause', PAUSE_LABEL)
    this.pauseButton.addEventListener('click', this.togglePause, { signal })
    // Online wars never pause (D10): the button is not offered.
    this.pauseButton.hidden = !match.canPause
    this.surrenderButton = button('tw-button tw-hud__surrender', SURRENDER_LABEL)
    this.surrenderButton.addEventListener('click', this.surrender, { signal })
    this.presenceBanner = el('div', 'tw-hud__presence')
    this.presenceBanner.setAttribute('role', 'status')
    this.presenceBanner.hidden = true
    this.linkBanner = el('div', 'tw-hud__link')
    this.linkBanner.setAttribute('role', 'alert')
    this.linkBanner.hidden = true

    const left = el('div', 'tw-hud__left')
    left.append(this.treasury.element)
    const centre = el('div', 'tw-hud__centre')
    centre.append(this.clock.element, this.ticker.element, this.presenceBanner, this.linkBanner)
    const controls = el('div', 'tw-hud__controls')
    controls.append(this.emptyCities.toggleButton, this.ledger.toggleButton, this.surrenderButton, this.pauseButton)
    // Both drop-downs hang under the right column, so neither can cover the controls that toggle them.
    const right = el('div', 'tw-hud__right')
    right.append(this.approval.element, this.taxDial.element, controls, this.emptyCities.element, this.ledger.element)
    const top = el('div', 'tw-hud__top')
    top.append(left, centre, right)

    const bars = el('div', 'tw-hud__bars')
    bars.append(this.arsenal.element, this.strikes.element)
    const bottom = el('div', 'tw-hud__bottom')
    bottom.append(bars)

    this.element.append(
      top,
      bottom,
      this.intel.element,
      this.buildMenu.element,
      this.toasts.element,
      el('div', 'tw-hud__paused', PAUSED_BANNER),
    )
    root.append(this.element)

    new GlobePointer(this.picker.surface, { click: this.inspect, hover: this.hoverGlobe, hoverEnd: this.endGlobeHover }, signal)
    window.addEventListener('keydown', this.onKeyDown, { signal })

    this.refreshDeploySites()
    const initial = match.privateState()
    if (initial !== null) this.onPrivate(initial)
    this.renderPause()
    this.subscriptions = [
      match.onPrivate(this.onPrivate),
      match.onEvents(this.onEvents),
      match.onCityChanged(this.onCityChanged),
      match.onResult(this.onResult),
      match.onPresence(this.onPresence),
      match.onLink(this.onLink),
    ]
  }

  /** Visual settings changed ("Reduce effects"; the palette they choose arrives through applyPalette). */
  applySettings(settings: VisualSettings): void {
    this.settings = settings
    applyUiTheme(this.element, this.palette, settings)
    this.ledger.applySettings(settings)
  }

  /** A new palette (a retune, or the player's palette setting): every themed colour and the ledger's canvas follow. */
  applyPalette(palette: Palette): void {
    this.palette = palette
    applyUiTheme(this.element, palette, this.settings)
    this.ledger.themeChanged()
  }

  /** Once per frame from the war screen's loop: drives the HUD's motion (the War Ledger's sheet). */
  update(dtSeconds: number): void {
    this.ledger.frame(dtSeconds)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    if (this.surrenderArmed !== null) clearTimeout(this.surrenderArmed)
    this.stopLinkCountdown()
    this.lifetime.abort()
    for (const unsubscribe of this.subscriptions) unsubscribe()
    this.element.remove()
  }

  // -------------------------------------------------------------------------------------------- match input

  private readonly onPrivate = (state: PlayerPrivateState): void => {
    this.treasury.update(state)
    this.approval.update(state.approval)
    this.clock.update(state)
    this.ledger.update(state)
    this.arsenal.update(state.money, state.revenuePerSec)
    this.strikes.update(state.money, state.revenuePerSec)
    this.buildMenu.setMoney(state.money)
    if (state.tax !== this.tax) {
      this.tax = state.tax
      this.taxDial.setCurrent(state.tax)
    }
  }

  /** HUD reactions to events beyond the headline every event may make. */
  private readonly reactions: { readonly [K in SimEventKind]?: (event: EventOf<K>) => void } = {
    commandRejected: (event) => {
      if (event.player === this.map.you) this.showRejection(event.seq, event.reason)
    },
    unitDeployed: (event) => {
      if (event.owner === this.map.you) this.ledger.recordDeploy(keyOf(UNIT_KEYS, event.unitType), event.cost)
    },
  }

  private readonly onEvents = (events: readonly SimEvent[]): void => {
    for (let i = 0; i < events.length; i++) {
      const event = events[i] as SimEvent
      const headline = this.headlines.write(event)
      if (headline !== null) this.ticker.push(headline)
      const react = this.reactions[event.kind] as ((event: SimEvent) => void) | undefined
      if (react !== undefined) react(event)
    }
  }

  private readonly onCityChanged = (index: CityIndex): void => {
    const sitesFlipped = this.sites.cityChanged(index)
    if (sitesFlipped) this.refreshDeploySites()
    this.buildMenu.cityChanged(index)
    this.emptyCities.cityChanged(index)
    const subject = this.hudSubject
    const dependsOn = this.intelDependsOn[subject.kind] as (subject: HudSubject, index: CityIndex, sitesFlipped: boolean) => boolean
    if (dependsOn(subject, index, sitesFlipped)) this.renderIntel()
  }

  private readonly onResult = (): void => {
    this.ended = true
    this.buildMenu.close()
    this.emptyCities.setOrdersOpen(false)
    this.element.classList.add('is-ended')
    this.pauseButton.disabled = true
    this.surrenderButton.disabled = true
    this.presenceBanner.hidden = true
    this.stopLinkCountdown()
    this.linkBanner.hidden = true
  }

  private readonly onPresence = (presence: RoomPresence): void => {
    // While this seat reconnects, the last report about the rival is stale; the room resends presence on the rejoin.
    const line = this.ended || !this.linkLive ? null : presenceLine(presence, this.match.you)
    this.presenceBanner.hidden = line === null
    if (line !== null) this.presenceBanner.textContent = line
  }

  /** Online: a dropped link puts orders on hold behind a countdown banner until the seat has rejoined. */
  private readonly onLink = (state: LinkState): void => {
    this.linkLive = state.kind === 'live'
    this.emptyCities.setOrdersOpen(this.ordersOpen())
    this.element.classList.toggle('is-reconnecting', !this.linkLive)
    if (state.kind === 'live') {
      this.stopLinkCountdown()
      this.linkBanner.hidden = true
      return
    }
    this.buildMenu.close()
    this.disarmSurrender()
    this.presenceBanner.hidden = true
    this.linkGiveUpAtMs = state.giveUpAtMs
    this.renderLinkBanner()
    if (this.linkCountdown === null) this.linkCountdown = setInterval(this.renderLinkBanner, LINK_COUNTDOWN_PERIOD_MS)
  }

  private readonly renderLinkBanner = (): void => {
    this.linkBanner.hidden = this.ended
    this.linkBanner.textContent = reconnectLine(this.linkGiveUpAtMs, Date.now())
  }

  private stopLinkCountdown(): void {
    if (this.linkCountdown !== null) clearInterval(this.linkCountdown)
    this.linkCountdown = null
  }

  /** Orders go out only while the war runs and (online) the seat's link is live. */
  private ordersOpen(): boolean {
    return !this.ended && this.linkLive
  }

  /** Two clicks concede: the first arms the button for a moment, the second sends the surrender (GDD §10). */
  private readonly surrender = (): void => {
    if (!this.ordersOpen()) return
    if (this.surrenderArmed === null) {
      this.surrenderButton.textContent = SURRENDER_CONFIRM_LABEL
      this.surrenderButton.classList.add('is-armed')
      this.surrenderArmed = setTimeout(this.disarmSurrender, SURRENDER_ARM_MS)
      return
    }
    this.disarmSurrender()
    this.surrenderButton.disabled = true
    this.match.sendCommand({ kind: 'surrender' })
  }

  private readonly disarmSurrender = (): void => {
    if (this.surrenderArmed !== null) clearTimeout(this.surrenderArmed)
    this.surrenderArmed = null
    this.surrenderButton.textContent = SURRENDER_LABEL
    this.surrenderButton.classList.remove('is-armed')
  }

  private showRejection(seq: number, reason: RejectReason): void {
    const label = this.pending.take(seq)
    const copy = REJECT_COPY[reason].long
    this.toasts.show(label === null ? copy : `${label}: ${copy}`, 'reject')
  }

  private refreshDeploySites(): void {
    for (const tile of this.arsenal.tiles) tile.setSiteState(this.sites.siteState(tile.item.key))
  }

  // -------------------------------------------------------------------------------------------- intel

  /**
   * Whether a change to city `index` can alter the Intel card of the current subject (a World War changes many cities
   * per view frame; only the shown card's own inputs re-render it). Read after the build menu saw the change.
   */
  private readonly intelDependsOn: {
    readonly [K in HudSubject['kind']]: (subject: SubjectOf<K>, index: CityIndex, sitesFlipped: boolean) => boolean
  } = {
    none: (_subject, index) => this.globeCity === index,
    item: (subject, _index, sitesFlipped) => sitesFlipped && ITEM_SHOWS_SITE[subject.item.kind],
    // A building card describes the open menu's city; with the menu closed the hovered globe city's card shows.
    building: (_subject, index) => {
      const city = this.buildMenu.openCity
      return city === null ? this.globeCity === index : city.index === index
    },
  }

  private readonly itemCards: { readonly [K in BarItem['kind']]: (item: ItemOf<K>) => InfoCard } = {
    unit: (item) => unitCard(item, this.sites.siteState(item.key)),
    strike: (item) => strikeCard(item),
  }

  /** The card of a HUD subject; null when there is none (a build option is only described while its menu is open). */
  private readonly subjectCards: { readonly [K in HudSubject['kind']]: (subject: SubjectOf<K>) => InfoCard | null } = {
    none: () => null,
    item: (subject) => (this.itemCards[subject.item.kind] as (item: BarItem) => InfoCard)(subject.item),
    building: (subject) => {
      const city = this.buildMenu.openCity
      if (city === null) return null
      return buildingCard(subject.key, city, buildObjection(subject.key, city, this.match.cityState(city.index), this.map.you))
    },
  }

  private readonly hoverItem = (item: BarItem | null): void => {
    if (item !== null) this.setHudSubject({ kind: 'item', item })
    else if (this.hudSubject.kind === 'item') this.setHudSubject(NO_SUBJECT)
  }

  private readonly hoverBuilding = (key: BuildingKey | null): void => {
    if (key !== null) this.setHudSubject({ kind: 'building', key })
    else if (this.hudSubject.kind === 'building') this.setHudSubject(NO_SUBJECT)
  }

  private readonly hoverGlobe = (clientX: number, clientY: number): void => {
    const index = this.picker.cityAt(clientX, clientY)
    if (index === this.globeCity) return
    this.globeCity = index
    if (this.hudSubject.kind === 'none') this.renderIntel()
  }

  private readonly endGlobeHover = (): void => {
    if (this.globeCity === null) return
    this.globeCity = null
    if (this.hudSubject.kind === 'none') this.renderIntel()
  }

  private setHudSubject(subject: HudSubject): void {
    this.hudSubject = subject
    this.renderIntel()
  }

  /** The HUD subject's card, else the hovered globe city's, else the idle panel. */
  private renderIntel(): void {
    const subject = this.hudSubject
    const card = (this.subjectCards[subject.kind] as (subject: HudSubject) => InfoCard | null)(subject) ?? this.globeCityCard()
    if (card === null) this.intel.clear()
    else this.intel.show(card)
  }

  private globeCityCard(): InfoCard | null {
    const index = this.globeCity
    if (index === null) return null
    const city = this.map.city(index)
    return cityCard(city, this.match.cityState(index), buildingSlots(city), this.map)
  }

  // -------------------------------------------------------------------------------------------- orders

  /** A tile or hotkey buys at once; the item's doctrine decides where it lands (GDD §7.1). */
  private readonly buy = (item: BarItem): void => {
    if (!this.ordersOpen()) return
    this.buildMenu.close()
    const seq =
      item.kind === 'unit'
        ? this.match.sendCommand({ kind: 'deploy', unitType: typeIdOf(UNIT_KEYS, item.key) })
        : this.match.sendCommand({ kind: 'strike', strikeType: typeIdOf(STRIKE_KEYS, item.key) })
    this.pending.record(seq, item.presentation.name)
  }

  private readonly build = (building: BuildingKey, city: MapCityView): void => {
    if (!this.ordersOpen()) return
    const seq = this.match.sendCommand({ kind: 'build', buildingType: typeIdOf(BUILDING_KEYS, building), city: city.simId })
    this.pending.record(seq, `${BUILDING_PRESENTATION[building].name} in ${city.name}`)
    this.buildMenu.close()
  }

  private readonly selectTax = (setting: TaxSetting): void => {
    if (!this.ordersOpen() || setting === this.tax) return
    const seq = this.match.sendCommand({ kind: 'setTax', setting })
    this.pending.record(seq, TAX_CHANGE_LABEL)
  }

  /** Both globe clicks and empty-city selections go through this one menu and the same build command. */
  private readonly openCity = (index: CityIndex, clientX: number, clientY: number): void => {
    if (!this.ordersOpen()) return
    if (this.match.cityState(index).owner !== this.map.you) {
      this.buildMenu.close()
      return
    }
    this.emptyCities.setOpen(false)
    this.ledger.setOpen(false)
    this.buildMenu.open(index, clientX, clientY)
  }

  private readonly openEmptyCity = (index: CityIndex, clientX: number, clientY: number): void => {
    this.openCity(index, clientX, clientY)
    if (this.buildMenu.isOpen) this.buildMenu.focusFirstOption()
  }

  /** A click on the globe: an owned city opens its build menu; anything else closes it. */
  private readonly inspect = (clientX: number, clientY: number): void => {
    if (!this.ordersOpen()) return
    const index = this.picker.cityAt(clientX, clientY)
    if (index === null) this.buildMenu.close()
    else this.openCity(index, clientX, clientY)
  }

  // -------------------------------------------------------------------------------------------- keys, pause, drop-downs

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.ctrlKey || event.metaKey || event.altKey || isTextEntry(event.target)) return
    const item = ITEMS_BY_HOTKEY.get(event.code)
    if (item !== undefined) {
      event.preventDefault()
      if (!event.repeat) this.buy(item)
      return
    }
    if (event.code === RECENTRE_KEY) {
      event.preventDefault()
      const capital = this.map.capital(this.map.you)
      if (!event.repeat) this.picker.focus(capital.lat, capital.lon)
      return
    }
    if (event.code === ESCAPE_KEY) {
      event.preventDefault()
      if (this.buildMenu.isOpen) this.buildMenu.close()
      else if (this.emptyCities.open) this.emptyCities.setOpen(false)
      else if (this.ledger.open) this.ledger.setOpen(false)
      else if (!event.repeat && this.match.canPause) this.togglePause()
    }
  }

  private readonly togglePause = (): void => {
    if (this.pauseInFlight || this.ended) return
    this.pauseInFlight = true
    this.pauseButton.disabled = true
    const request = this.match.paused ? this.match.resume() : this.match.pause()
    // A failed pause/resume rejects unhandled on purpose: the room connection is broken (fail loud).
    void request
      .finally(() => {
        this.pauseInFlight = false
      })
      .then(() => this.renderPause())
  }

  private renderPause(): void {
    if (this.disposed) return
    const paused = this.match.paused
    this.pauseButton.textContent = paused ? RESUME_LABEL : PAUSE_LABEL
    this.pauseButton.setAttribute('aria-pressed', paused ? 'true' : 'false')
    this.pauseButton.disabled = this.ended
    this.element.classList.toggle('is-paused', paused)
  }

  /** The empty-cities list is opening: the build menu and the ledger make way. */
  private readonly closeCityPanels = (): void => {
    this.buildMenu.close()
    this.ledger.setOpen(false)
  }

  /** The ledger is opening: the build menu and the empty-cities list make way. */
  private readonly ledgerOpened = (): void => {
    this.emptyCities.setOpen(false)
    this.buildMenu.close()
  }
}
